import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Node,
  Edge,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
} from '@xyflow/react';
import {
  Task,
  Note,
  Goal,
  Habit,
  Domain,
  CanvasNodeData,
  Event,
  Contact,
  EntityType,
  InboxItem,
} from '../types';

import { taskService } from '../services/task.service';
import { goalService } from '../services/goal.service';
import { checkAndCompleteGoal, revertGoalIfNeeded } from '../services/goal.service';
import { domainService } from '../services/domain.service';
import { eventService } from '../services/event.service';
import { noteService } from '../services/note.service';
import { contactService } from '../services/contact.service';
import { habitService } from '../services/habit.service';
import { inboxService } from '../services/inbox.service';
import { canvasService, type Brain } from '../services/canvas.service';
import { supabase } from '../lib/supabase';
import { generateId } from '../utils';

// FK synchronization types
export interface FkChange {
  table: 'tasks' | 'events' | 'goals';
  entityId: string;
  field: string; // 'goal_id', 'event_id', 'parent_goal_id'
  previousValue: string | null;
}

export interface CanvasSnapshot {
  nodes: Node<CanvasNodeData>[];
  edges: Edge[];
  fkChanges?: FkChange[];
}

const initialNodes: Node<CanvasNodeData>[] = [
  {
    id: 'node-t1',
    type: 'taskNode',
    position: { x: 250, y: 100 },
    data: { entityId: 't1' },
  },
  {
    id: 'node-n1',
    type: 'noteNode',
    position: { x: 600, y: 200 },
    data: { entityId: 'n1' },
  },
  {
    id: 'node-g1',
    type: 'goalNode',
    position: { x: 250, y: 350 },
    data: { entityId: 'g1' },
  },
];

const initialEdges: Edge[] = [
  {
    id: 'e1',
    source: 'node-t1',
    target: 'node-g1',
    type: 'waypoint',
    data: { waypoints: [] },
    style: { stroke: '#52525b' },
  },
  {
    id: 'e2',
    source: 'node-n1',
    target: 'node-g1',
    type: 'waypoint',
    data: { waypoints: [] },
    style: { stroke: '#52525b' },
  },
];

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

export interface ModalState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  entityType?: EntityType;
  entityId?: string;
  initialData?: any;
  onSuccess?: () => void;
}

interface AppState {
  // Structured Data
  tasks: Task[];
  notes: Note[];
  goals: Goal[];
  habits: Habit[];
  domains: Domain[];
  events: Event[];
  contacts: Contact[];
  inboxItems: InboxItem[];

  // Canvas Data
  nodes: Node<CanvasNodeData>[];
  edges: Edge[];

  // Modal State
  modalState: ModalState;
  openModal: (
    mode: 'create' | 'edit',
    entityType?: EntityType,
    entityId?: string,
    initialData?: any,
    onSuccess?: () => void
  ) => void;
  closeModal: () => void;

  // Feedback Modal State
  isFeedbackModalOpen: boolean;
  openFeedbackModal: () => void;
  closeFeedbackModal: () => void;

  // Task specific state
  tasksLoading: boolean;
  tasksError: string | null;
  fetchTasks: () => Promise<void>;

  // Note specific state
  notesLoading: boolean;
  notesError: string | null;
  fetchNotes: () => Promise<void>;

  // Domain specific state
  domainsLoading: boolean;
  domainsError: string | null;
  fetchDomains: () => Promise<void>;

  // Goal specific state
  goalsLoading: boolean;
  goalsError: string | null;
  fetchGoals: () => Promise<void>;

  // Event specific state
  eventsLoading: boolean;
  eventsError: string | null;
  fetchEvents: () => Promise<void>;

  // Contact specific state
  contactsLoading: boolean;
  contactsError: string | null;
  fetchContacts: () => Promise<void>;

  // Habit specific state
  habitsLoading: boolean;
  habitsError: string | null;
  fetchHabits: () => Promise<void>;

  // Inbox specific state
  inboxItemsLoading: boolean;
  inboxItemsError: string | null;
  fetchInboxItems: () => Promise<void>;
  /** AI-021: In-memory patch — no DB write. Used after the Edge Function has already
   * persisted the result server-side. Keeps the store in sync without a round-trip. */
  updateInboxItemLocal: (id: string, updates: Partial<InboxItem>) => void;

  // Generic CRUD Actions
  // VB-029: returns the created entity's ID (null on visitor mode fallback or unknown type)
  addEntity: (type: EntityType, payload: any) => Promise<string | null>;
  updateEntity: (type: EntityType, id: string, payload: any) => Promise<void>;
  deleteEntity: (type: EntityType, id: string) => Promise<void>;

  // Specific Actions
  updateTaskStatus: (id: string, status: Task['status']) => void;
  toggleHabit: (id: string, date: string) => void;

  // Canvas Actions
  onNodesChange: OnNodesChange<Node<CanvasNodeData>>;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  addNode: (node: Node<CanvasNodeData>) => void;
  // AI-034: programmatic edge insertion (symmetric with addNode)
  addEdge: (edge: Edge) => void;
  updateNodeData: (id: string, data: Partial<CanvasNodeData>) => void;
  addToCanvas: (entityId: string, type: string, position?: { x: number; y: number }) => void;
  removeFromCanvas: (nodeId: string) => void;
  removeEdge: (edgeId: string) => void;
  // VB-032.5 — Waypoint edges
  updateEdgeWaypoints: (edgeId: string, waypoints: Array<{ x: number; y: number }>) => void;
  showWaypoints: boolean;
  toggleShowWaypoints: () => void;

  // Canvas History (VB-017 — undo/redo canvas structure)
  canvasHistory: {
    past: CanvasSnapshot[];
    future: CanvasSnapshot[];
  };
  _pendingSnapshot: CanvasSnapshot | null;
  saveCanvasCheckpoint: () => void;
  commitCanvasCheckpoint: () => void;
  pushToCanvasHistory: (fkChanges?: FkChange[]) => void;
  undoCanvas: () => void;
  redoCanvas: () => void;

  // VB-021 — Canvas remote sync (VB-023: remote is sole source of truth, no localStorage)
  canvasLoading: boolean;
  hasLoadedRemoteCanvas: boolean;
  isHydratingCanvas: boolean;
  loadCanvasFromRemote: (userId: string) => Promise<void>;

  // Multi-Brain
  brains: Brain[];
  activeBrainId: string | null;
  brainsLoaded: boolean;
  switchBrain: (userId: string, brainId: string) => Promise<void>;
  createBrain: (userId: string, title: string) => Promise<Brain | null>;
  renameBrain: (brainId: string, newTitle: string) => Promise<boolean>;
  deleteBrain: (brainId: string) => Promise<boolean>;

  // VB-020 — Example canvas injection
  loadExampleCanvas: () => Promise<void>;

  // Navigation
  focusedEntityId: string | null;
  setFocusedEntityId: (id: string | null) => void;
  // AI-034 — Tree-level focus: fit the viewport to a group of newly projected nodes
  focusedTreeNodeIds: string[] | null;
  setFocusedTreeNodeIds: (ids: string[] | null) => void;

  // VB-032 — Synapse pipeline progression (survives component unmount)
  synapsePipelineExtractionDoneIds: Set<string>;
  synapsePipelineCanvasProjectedIds: Set<string>;
  markExtractionDone: (itemId: string) => void;
  markCanvasProjected: (itemId: string) => void;

  // Toast notifications
  toasts: Toast[];
  addToast: (message: string, type: Toast['type']) => void;
  removeToast: (id: string) => void;

  // Goal completion modal
  pendingCompletedGoalId: string | null;
  setPendingCompletedGoalId: (id: string | null) => void;
}

/** VB-017 — Structural comparison of two canvas snapshots.
 *  Ignores transient ReactFlow properties (selected, dragging, measured). */
function canvasStructureChanged(a: CanvasSnapshot, b: CanvasSnapshot): boolean {
  if (a.nodes.length !== b.nodes.length || a.edges.length !== b.edges.length) return true;
  for (let i = 0; i < a.nodes.length; i++) {
    const na = a.nodes[i],
      nb = b.nodes[i];
    if (na.id !== nb.id) return true;
    if (na.position.x !== nb.position.x || na.position.y !== nb.position.y) return true;
    if (na.type === 'zoneNode') {
      if (na.data?.label !== nb.data?.label) return true;
      if ((na.style as any)?.width !== (nb.style as any)?.width) return true;
      if ((na.style as any)?.height !== (nb.style as any)?.height) return true;
    }
  }
  for (let i = 0; i < a.edges.length; i++) {
    if (a.edges[i].id !== b.edges[i].id) return true;
  }
  return false;
}

/** VB-017 — Strip transient ReactFlow properties from canvas snapshots.
 *  Ensures undo/redo only captures structural state (positions, edges, zone dimensions),
 *  not selection, drag, or measurement state that varies between renders.
 *  VB-021 — Exported so App.tsx can use it for the auto-save debounce. */
export function cleanCanvasSnapshot(nodes: Node<CanvasNodeData>[], edges: Edge[]): CanvasSnapshot {
  return {
    nodes: nodes.map((n) => {
      const c: Record<string, any> = { ...n };
      delete c.selected;
      delete c.dragging;
      delete c.measured;
      delete c.resizing;
      return c as Node<CanvasNodeData>;
    }),
    edges: edges.map((e) => {
      const c: Record<string, any> = { ...e };
      delete c.selected;
      return c as Edge;
    }),
  };
}

const getCollectionKey = (type: EntityType) => {
  if (type === 'inbox') return 'inboxItems';
  return `${type}s` as keyof AppState;
};

// VB-023 — One-time purge of legacy canvas data from localStorage.
// Canvas is now exclusively persisted in Supabase; any nodes/edges stored
// locally by previous versions are removed to prevent stale state re-hydration.
try {
  const STORE_KEY = 'second-brain-storage';
  const raw = localStorage.getItem(STORE_KEY);
  if (raw) {
    const parsed = JSON.parse(raw);
    if (parsed?.state && ('nodes' in parsed.state || 'edges' in parsed.state)) {
      delete parsed.state.nodes;
      delete parsed.state.edges;
      localStorage.setItem(STORE_KEY, JSON.stringify(parsed));
    }
  }
} catch {
  /* non-blocking */
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      tasks: [], // Initialisé vide avant fetch
      notes: [], // Initialisé vide avant fetch
      goals: [], // Initialisé vide avant fetch
      habits: [], // Initialisé vide avant fetch
      domains: [], // Initialisé vide avant fetch
      events: [], // Initialisé vide avant fetch
      contacts: [], // Initialisé vide avant fetch
      inboxItems: [],

      nodes: initialNodes,
      edges: initialEdges,

      focusedEntityId: null,
      setFocusedEntityId: (id) => set({ focusedEntityId: id }),
      focusedTreeNodeIds: null,
      setFocusedTreeNodeIds: (ids) => set({ focusedTreeNodeIds: ids }),

      // VB-032 — Synapse pipeline progression
      synapsePipelineExtractionDoneIds: new Set<string>(),
      synapsePipelineCanvasProjectedIds: new Set<string>(),
      markExtractionDone: (itemId) =>
        set((state) => ({
          synapsePipelineExtractionDoneIds: new Set(state.synapsePipelineExtractionDoneIds).add(
            itemId
          ),
        })),
      markCanvasProjected: (itemId) =>
        set((state) => ({
          synapsePipelineCanvasProjectedIds: new Set(state.synapsePipelineCanvasProjectedIds).add(
            itemId
          ),
        })),

      // Feedback Modal State
      isFeedbackModalOpen: false,
      openFeedbackModal: () => set({ isFeedbackModalOpen: true }),
      closeFeedbackModal: () => set({ isFeedbackModalOpen: false }),

      tasksLoading: false,
      tasksError: null,
      fetchTasks: async () => {
        set({ tasksLoading: true, tasksError: null });
        try {
          const tasks = await taskService.getTasks();
          set({ tasks, tasksLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération des tâches:', err);
          set({ tasksError: err.message || 'Erreur inconnue', tasksLoading: false });
        }
      },

      notesLoading: false,
      notesError: null,
      fetchNotes: async () => {
        set({ notesLoading: true, notesError: null });
        try {
          const notes = await noteService.getNotes();
          set({ notes, notesLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération des notes:', err);
          set({ notesError: err.message || 'Erreur inconnue', notesLoading: false });
        }
      },

      domainsLoading: false,
      domainsError: null,
      fetchDomains: async () => {
        set({ domainsLoading: true, domainsError: null });
        try {
          const fetchedDomains = await domainService.getDomains();
          set({ domains: fetchedDomains, domainsLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération des domaines:', err);
          set({ domainsError: err.message || 'Erreur inconnue', domainsLoading: false });
        }
      },

      goalsLoading: false,
      goalsError: null,
      fetchGoals: async () => {
        set({ goalsLoading: true, goalsError: null });
        try {
          const fetchedGoals = await goalService.getGoals();
          set({ goals: fetchedGoals, goalsLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération des objectifs:', err);
          set({ goalsError: err.message || 'Erreur inconnue', goalsLoading: false });
        }
      },

      eventsLoading: false,
      eventsError: null,
      fetchEvents: async () => {
        set({ eventsLoading: true, eventsError: null });
        try {
          const fetchedEvents = await eventService.getEvents();
          set({ events: fetchedEvents, eventsLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération des événements:', err);
          set({ eventsError: err.message || 'Erreur inconnue', eventsLoading: false });
        }
      },

      contactsLoading: false,
      contactsError: null,
      fetchContacts: async () => {
        set({ contactsLoading: true, contactsError: null });
        try {
          const contacts = await contactService.getContacts();
          set({ contacts, contactsLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération des contacts:', err);
          set({ contactsError: err.message || 'Erreur inconnue', contactsLoading: false });
        }
      },

      habitsLoading: false,
      habitsError: null,
      fetchHabits: async () => {
        set({ habitsLoading: true, habitsError: null });
        try {
          const habits = await habitService.getHabits();
          set({ habits, habitsLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération des habitudes:', err);
          set({ habitsError: err.message || 'Erreur inconnue', habitsLoading: false });
        }
      },

      inboxItemsLoading: false,
      inboxItemsError: null,
      fetchInboxItems: async () => {
        set({ inboxItemsLoading: true, inboxItemsError: null });
        try {
          const inboxItems = await inboxService.getInboxItems();
          set({ inboxItems, inboxItemsLoading: false });
        } catch (err: any) {
          console.error('Erreur lors de la récupération de la boîte de réception:', err);
          set({ inboxItemsError: err.message || 'Erreur inconnue', inboxItemsLoading: false });
        }
      },
      // AI-021: In-memory patch for when the DB has already been updated server-side
      // (after Edge Function call). Avoids a redundant DB round-trip.
      updateInboxItemLocal: (id, updates) => {
        set((state) => ({
          inboxItems: state.inboxItems.map((i) => (i.id === id ? { ...i, ...updates } : i)),
        }));
      },

      modalState: { isOpen: false, mode: 'create' },

      openModal: (mode, entityType, entityId, initialData, onSuccess) =>
        set({ modalState: { isOpen: true, mode, entityType, entityId, initialData, onSuccess } }),
      closeModal: () => set({ modalState: { isOpen: false, mode: 'create' } }),

      addEntity: async (type, payload) => {
        try {
          const { modalState } = get();
          const pendingConnection = modalState.initialData?.pendingConnection;
          let newEntityId: string | null = null;

          if (type === 'task') {
            const newTask = await taskService.createTask(payload);
            set((state) => ({ tasks: [newTask, ...state.tasks] }));
            newEntityId = newTask.id;
          } else if (type === 'note') {
            const newNote = await noteService.createNote(payload);
            set((state) => ({ notes: [newNote, ...state.notes] }));
            newEntityId = newNote.id;
          } else if (type === 'goal') {
            const newGoal = await goalService.createGoal(payload);
            set((state) => ({ goals: [newGoal, ...state.goals] }));
            newEntityId = newGoal.id;
          } else if (type === 'event') {
            const newEvent = await eventService.createEvent(payload);
            set((state) => ({ events: [newEvent, ...state.events] }));
            newEntityId = newEvent.id;
          } else if (type === 'contact') {
            const newContact = await contactService.createContact(payload);
            set((state) => ({ contacts: [newContact, ...state.contacts] }));
            newEntityId = newContact.id;
          } else if (type === 'habit') {
            const newHabit = await habitService.createHabit(payload);
            set((state) => ({ habits: [newHabit, ...state.habits] }));
            newEntityId = newHabit.id;
          } else if (type === 'inbox') {
            const newInboxItem = await inboxService.createInboxItem(payload);
            set((state) => ({ inboxItems: [newInboxItem, ...state.inboxItems] }));
            newEntityId = newInboxItem.id;
          } else {
            const key = getCollectionKey(type);
            const newItem = { ...payload, id: generateId() };
            set((state) => ({ [key]: [newItem, ...(state[key] as any[])] }));
            newEntityId = newItem.id;
          }

          if (pendingConnection && newEntityId) {
            get().addToCanvas(newEntityId, type, pendingConnection.position);

            const stateAfterNode = get();
            const createdNode = stateAfterNode.nodes.find((n) => n.data.entityId === newEntityId);
            if (createdNode) {
              const isTargetOrigin = pendingConnection.handleType === 'target';

              set((state) => ({
                edges: [
                  ...state.edges,
                  {
                    id: `edge-${pendingConnection.sourceId}-${createdNode.id}-${Date.now()}`,
                    source: isTargetOrigin ? createdNode.id : pendingConnection.sourceId,
                    sourceHandle: isTargetOrigin
                      ? 'bottom'
                      : pendingConnection.sourceHandle || null,
                    target: isTargetOrigin ? pendingConnection.sourceId : createdNode.id,
                    targetHandle: isTargetOrigin ? pendingConnection.sourceHandle || 'top' : 'top',
                    // VB-024B.2: no animated on connect-to-create edges (SVG stroke-dasharray causes lag on zoom)
                    // Manual connections between existing nodes (onConnect) already don't use animated.
                    style: { stroke: '#52525b' },
                  },
                ],
              }));
            }
          }
          // VB-029: return the created entity ID for link resolution
          return newEntityId;
        } catch (error: any) {
          console.error(`Erreur réseau (création ${type}):`, error);
          throw error;
        }
      },

      updateEntity: async (type, id, payload) => {
        try {
          if (type === 'task') {
            // Optimistic completedAt patch for immediate canvas/view reactivity
            if (payload.status !== undefined) {
              set((state) => ({
                tasks: state.tasks.map((t) => {
                  if (t.id !== id) return t;
                  const goingDone = payload.status === 'done' && t.status !== 'done';
                  const leavingDone = payload.status !== 'done' && t.status === 'done';
                  return {
                    ...t,
                    status: payload.status,
                    completedAt: goingDone
                      ? new Date().toISOString()
                      : leavingDone
                        ? null
                        : t.completedAt,
                  };
                }),
              }));
            }
            const updatedTask = await taskService.updateTask(id, payload);
            set((state) => ({
              tasks: state.tasks.map((t) => (t.id === id ? updatedTask : t)),
              nodes: state.nodes.map((n) =>
                n.data?.entityId === id ? { ...n, data: { ...n.data, ...updatedTask } } : n
              ),
            }));
            return;
          }
          if (type === 'note') {
            const updatedNote = await noteService.updateNote(id, payload);
            set((state) => ({
              notes: state.notes.map((n) => (n.id === id ? updatedNote : n)),
              nodes: state.nodes.map((n) =>
                n.data?.entityId === id ? { ...n, data: { ...n.data, ...updatedNote } } : n
              ),
            }));
            return;
          }
          if (type === 'goal') {
            const updatedGoal = await goalService.updateGoal(id, payload);
            set((state) => ({
              goals: state.goals.map((g) => (g.id === id ? updatedGoal : g)),
              nodes: state.nodes.map((n) =>
                n.data?.entityId === id ? { ...n, data: { ...n.data, ...updatedGoal } } : n
              ),
            }));
            return;
          }
          if (type === 'event') {
            const updatedEvent = await eventService.updateEvent(id, payload);
            set((state) => ({
              events: state.events.map((e) => (e.id === id ? updatedEvent : e)),
              nodes: state.nodes.map((n) =>
                n.data?.entityId === id ? { ...n, data: { ...n.data, ...updatedEvent } } : n
              ),
            }));
            return;
          }
          if (type === 'contact') {
            const updatedContact = await contactService.updateContact(id, payload);
            set((state) => ({
              contacts: state.contacts.map((c) => (c.id === id ? updatedContact : c)),
              nodes: state.nodes.map((n) =>
                n.data?.entityId === id ? { ...n, data: { ...n.data, ...updatedContact } } : n
              ),
            }));
            return;
          }
          if (type === 'habit') {
            const updatedHabit = await habitService.updateHabit(id, payload);
            set((state) => ({
              habits: state.habits.map((h) => (h.id === id ? updatedHabit : h)),
            }));
            return;
          }
          if (type === 'inbox') {
            const updatedInboxItem = await inboxService.updateInboxItem(id, payload);
            set((state) => ({
              inboxItems: state.inboxItems.map((i) => (i.id === id ? updatedInboxItem : i)),
            }));
            return;
          }
          set((state) => {
            const key = getCollectionKey(type);
            return {
              [key]: (state[key] as any[]).map((item: any) =>
                item.id === id ? { ...item, ...payload } : item
              ),
            };
          });
        } catch (error: any) {
          console.error(`Erreur réseau (modification ${type}):`, error);
          throw error;
        }
      },

      deleteEntity: async (type, id) => {
        try {
          if (type === 'task') {
            await taskService.deleteTask(id);
            set((state) => ({
              tasks: state.tasks.filter((t) => t.id !== id),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            }));
            return;
          }
          if (type === 'note') {
            await noteService.deleteNote(id);
            set((state) => ({
              notes: state.notes.filter((n) => n.id !== id),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            }));
            return;
          }
          if (type === 'goal') {
            await goalService.deleteGoal(id);
            set((state) => ({
              goals: state.goals.filter((g) => g.id !== id),
              tasks: state.tasks.map((t) => (t.goalId === id ? { ...t, goalId: undefined } : t)),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            }));
            return;
          }
          if (type === 'event') {
            await eventService.deleteEvent(id);
            set((state) => ({
              events: state.events.filter((e) => e.id !== id),
              tasks: state.tasks.map((t) => (t.eventId === id ? { ...t, eventId: undefined } : t)),
              notes: state.notes.map((n) => (n.eventId === id ? { ...n, eventId: undefined } : n)),
              contacts: state.contacts.map((c) =>
                c.eventIds ? { ...c, eventIds: c.eventIds.filter((eid) => eid !== id) } : c
              ),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            }));
            return;
          }
          if (type === 'contact') {
            await contactService.deleteContact(id);
            set((state) => ({
              contacts: state.contacts.filter((c) => c.id !== id),
              events: state.events.map((e) =>
                e.contactIds ? { ...e, contactIds: e.contactIds.filter((cid) => cid !== id) } : e
              ),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            }));
            return;
          }
          if (type === 'habit') {
            await habitService.deleteHabit(id);
            set((state) => ({
              habits: state.habits.filter((h) => h.id !== id),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            }));
            return;
          }
          if (type === 'inbox') {
            const inboxItem = get().inboxItems.find((i) => i.id === id);
            await inboxService.deleteInboxItem(id, inboxItem?.audioUrl);
            set((state) => ({
              inboxItems: state.inboxItems.filter((i) => i.id !== id),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            }));
            return;
          }
          set((state) => {
            const key = getCollectionKey(type);
            return {
              [key]: (state[key] as any[]).filter((item: any) => item.id !== id),
              nodes: state.nodes.filter((n) => n.data?.entityId !== id),
            };
          });
        } catch (error: any) {
          console.error(`Erreur réseau (suppression ${type}):`, error);
        }
      },

      updateTaskStatus: async (id, status) => {
        // Optimistic UI update
        set((state) => ({
          tasks: state.tasks.map((t) => {
            if (t.id !== id) return t;
            const goingDone = status === 'done' && t.status !== 'done';
            const leavingDone = status !== 'done' && t.status === 'done';
            return {
              ...t,
              status,
              completedAt: goingDone
                ? new Date().toISOString()
                : leavingDone
                  ? null
                  : t.completedAt,
            };
          }),
        }));
        try {
          await taskService.updateTask(id, { status });

          // Behaviour A: if task was marked done and linked to a goal, check goal auto-completion
          if (status === 'done') {
            const task = get().tasks.find((t) => t.id === id);
            if (task?.goalId) {
              const {
                data: { user },
              } = await supabase.auth.getUser();
              if (user) {
                const completed = await checkAndCompleteGoal(
                  task.goalId,
                  user.id,
                  get().tasks,
                  get().events,
                  get().goals
                );
                if (completed) {
                  set((state) => ({
                    goals: state.goals.map((g) =>
                      g.id === task.goalId
                        ? {
                            ...g,
                            status: 'achieved' as const,
                            completedAt: new Date().toISOString(),
                          }
                        : g
                    ),
                  }));
                  get().setPendingCompletedGoalId(task.goalId);
                }
              }
            }
          } else {
            // Behaviour A (revert): if task was un-done and linked to an achieved goal, revert it
            const task = get().tasks.find((t) => t.id === id);
            if (task?.goalId) {
              const {
                data: { user },
              } = await supabase.auth.getUser();
              if (user) {
                const reverted = await revertGoalIfNeeded(
                  task.goalId,
                  user.id,
                  get().tasks,
                  get().events,
                  get().goals
                );
                if (reverted) {
                  set((state) => ({
                    goals: state.goals.map((g) =>
                      g.id === task.goalId
                        ? { ...g, status: 'active' as const, completedAt: null }
                        : g
                    ),
                  }));
                }
              }
            }
          }
        } catch (error) {
          console.error('Erreur mise à jour statut tâche:', error);
          // Note: Real app would revert the state on failure here
        }
      },

      toggleHabit: async (id, date) => {
        const habit = get().habits.find((h) => h.id === id);
        if (!habit) return;
        const isCompleted = habit.completedDates.includes(date);

        // Optimistic update
        set((state) => ({
          habits: state.habits.map((h) => {
            if (h.id === id) {
              return {
                ...h,
                completedDates: isCompleted
                  ? h.completedDates.filter((d) => d !== date)
                  : [...h.completedDates, date],
              };
            }
            return h;
          }),
        }));

        try {
          await habitService.toggleHabitLog(id, date, isCompleted);
        } catch (error) {
          console.error('Erreur toggleHabit:', error);
          // Revert on failure
          set((state) => ({
            habits: state.habits.map((h) => {
              if (h.id === id) {
                return {
                  ...h,
                  completedDates: isCompleted
                    ? [...h.completedDates, date]
                    : h.completedDates.filter((d) => d !== date),
                };
              }
              return h;
            }),
          }));
        }
      },

      // VB-017 — Canvas history (checkpoint-based for reliable undo/redo)
      canvasHistory: { past: [], future: [] },
      _pendingSnapshot: null,

      // VB-021 — Canvas remote sync (session-only, not persisted)
      canvasLoading: false,
      hasLoadedRemoteCanvas: false,
      isHydratingCanvas: false,

      // Multi-Brain
      brains: [],
      activeBrainId: null,
      brainsLoaded: false,

      saveCanvasCheckpoint: () => {
        const { nodes, edges } = get();
        set({ _pendingSnapshot: cleanCanvasSnapshot(nodes, edges) });
      },

      commitCanvasCheckpoint: () => {
        const pending = get()._pendingSnapshot;
        if (!pending) return;
        set({ _pendingSnapshot: null });
        const { nodes, edges, canvasHistory } = get();
        if (!canvasStructureChanged(pending, { nodes, edges })) return;
        const newPast = [...canvasHistory.past, pending];
        if (newPast.length > 50) newPast.shift();
        set({ canvasHistory: { past: newPast, future: [] } });
      },

      pushToCanvasHistory: (fkChanges) => {
        set({ _pendingSnapshot: null });
        const { nodes, edges, canvasHistory } = get();
        const snapshot: CanvasSnapshot = { ...cleanCanvasSnapshot(nodes, edges), fkChanges };
        const newPast = [...canvasHistory.past, snapshot];
        if (newPast.length > 50) newPast.shift();
        set({ canvasHistory: { past: newPast, future: [] } });
      },

      undoCanvas: () => {
        const { canvasHistory, nodes, edges } = get();
        if (!canvasHistory.past.length) return;
        const previous = canvasHistory.past[canvasHistory.past.length - 1];
        const newPast = canvasHistory.past.slice(0, -1);
        const currentClean = cleanCanvasSnapshot(nodes, edges);

        // Propagate fkChanges into future so redo can re-apply them
        const currentSnapshot: CanvasSnapshot = {
          ...currentClean,
          fkChanges: previous.fkChanges,
        };
        const newFuture = [currentSnapshot, ...canvasHistory.future];

        set({
          nodes: previous.nodes,
          edges: previous.edges,
          canvasHistory: { past: newPast, future: newFuture },
          _pendingSnapshot: null,
        });

        // Restore FKs if fkChanges exist (fire-and-forget, optimistic)
        if (previous.fkChanges?.length) {
          import('../lib/canvas-fk-sync').then(({ applyFkChange }) => {
            for (const change of previous.fkChanges!) {
              applyFkChange(change.entityId, change.table, change.field, change.previousValue);
            }
          });
        }
      },

      redoCanvas: () => {
        const { canvasHistory, nodes, edges } = get();
        if (!canvasHistory.future.length) return;
        const [next, ...restFuture] = canvasHistory.future;
        const currentClean = cleanCanvasSnapshot(nodes, edges);

        // Propagate fkChanges into past so undo can reverse them again
        const currentSnapshot: CanvasSnapshot = {
          ...currentClean,
          fkChanges: next.fkChanges,
        };
        const newPast = [...canvasHistory.past, currentSnapshot];

        set({
          nodes: next.nodes,
          edges: next.edges,
          canvasHistory: { past: newPast, future: restFuture },
          _pendingSnapshot: null,
        });

        // Re-apply FK deletions (set to null = redo the suppression)
        if (next.fkChanges?.length) {
          import('../lib/canvas-fk-sync').then(({ applyFkChange }) => {
            for (const change of next.fkChanges!) {
              applyFkChange(change.entityId, change.table, change.field, null);
            }
          });
        }
      },

      // VB-021/VB-023 — Load canvas from Supabase. Remote is the sole source of truth.
      // Guard flag (isHydratingCanvas) blocks the auto-save debounce during hydration.
      loadCanvasFromRemote: async (userId) => {
        set({ canvasLoading: true, isHydratingCanvas: true });
        try {
          const brains = await canvasService.loadBrains(userId);
          const defaultBrain = brains.find((b) => b.isDefault) || brains[0] || null;
          set({ brains, activeBrainId: defaultBrain?.id || null, brainsLoaded: true });

          let targetBrainId = defaultBrain?.id;
          if (!targetBrainId) {
            const newBrain = await canvasService.createBrain(userId, 'Mon Visual Brain');
            if (newBrain) {
              await supabase
                .from('visual_brain_canvases')
                .update({ is_default: true })
                .eq('id', newBrain.id);
              newBrain.isDefault = true;
              set({ brains: [newBrain], activeBrainId: newBrain.id });
              targetBrainId = newBrain.id;
            }
          }

          if (targetBrainId) {
            const data = await canvasService.loadCanvas(userId, targetBrainId);
            if (data) {
              set({ nodes: data.nodes || [], edges: data.edges || [] });
            }
          }
        } catch (err) {
          console.error('[store] loadCanvasFromRemote:', err);
        } finally {
          set({ canvasLoading: false, hasLoadedRemoteCanvas: true, isHydratingCanvas: false });
        }
      },

      switchBrain: async (userId: string, brainId: string) => {
        const currentState = get();
        const currentBrainId = currentState.activeBrainId;

        // Sauvegarder le brain actuel avant de switch
        if (currentBrainId && currentBrainId !== brainId) {
          await canvasService.saveCanvas(
            userId,
            currentBrainId,
            JSON.parse(JSON.stringify(currentState.nodes)),
            JSON.parse(JSON.stringify(currentState.edges))
          );
        }

        // Charger le nouveau brain
        set({ canvasLoading: true, activeBrainId: brainId });
        const data = await canvasService.loadCanvas(userId, brainId);
        set({
          nodes: data?.nodes || [],
          edges: data?.edges || [],
          canvasLoading: false,
          hasLoadedRemoteCanvas: true,
        });
      },

      createBrain: async (userId: string, title: string) => {
        const brain = await canvasService.createBrain(userId, title);
        if (brain) set((state) => ({ brains: [...state.brains, brain] }));
        return brain;
      },

      renameBrain: async (brainId: string, newTitle: string) => {
        const success = await canvasService.renameBrain(brainId, newTitle);
        if (success)
          set((state) => ({
            brains: state.brains.map((b) => (b.id === brainId ? { ...b, title: newTitle } : b)),
          }));
        return success;
      },

      deleteBrain: async (brainId: string) => {
        const success = await canvasService.deleteBrain(brainId);
        if (success) set((state) => ({ brains: state.brains.filter((b) => b.id !== brainId) }));
        return success;
      },

      // VB-020 — Inject a lightweight example canvas with real Supabase entities.
      // VB-020 fix: idempotent — reuses existing example entities instead of always creating new ones.
      loadExampleCanvas: async () => {
        try {
          const ts = Date.now();
          const state = get();

          // Retrieve or create each example entity (deduplication by title)
          const existingGoal = state.goals.find((g) => g.title === 'Mon premier objectif');
          const goal =
            existingGoal ??
            (await goalService.createGoal({
              title: 'Mon premier objectif',
              description: 'Comprendre comment connecter mes idées dans le Visual Brain.',
              status: 'active',
            }));
          if (!existingGoal) set((s) => ({ goals: [goal, ...s.goals] }));

          const existingTask = state.tasks.find((t) => t.title === 'Explorer le Visual Brain');
          const task =
            existingTask ??
            (await taskService.createTask({
              title: 'Explorer le Visual Brain',
              status: 'todo',
              priority: 'medium',
              goalId: goal.id,
            }));
          if (!existingTask) set((s) => ({ tasks: [task, ...s.tasks] }));

          const existingNote = state.notes.find((n) => n.title === 'Ma première idée');
          const note =
            existingNote ??
            (await noteService.createNote({
              title: 'Ma première idée',
              content:
                'Connectez vos notes à vos objectifs pour organiser visuellement votre réflexion.',
            }));
          if (!existingNote) set((s) => ({ notes: [note, ...s.notes] }));

          const existingEvent = state.events.find((e) => e.title === 'Lancement de mon projet');
          const event =
            existingEvent ??
            (await eventService.createEvent({
              title: 'Lancement de mon projet',
              date: new Date().toISOString().split('T')[0],
            }));
          if (!existingEvent) set((s) => ({ events: [event, ...s.events] }));

          // Build canvas nodes with pre-defined positions
          const goalNodeId = `node-${goal.id}-${ts}`;
          const taskNodeId = `node-${task.id}-${ts + 1}`;
          const noteNodeId = `node-${note.id}-${ts + 2}`;
          const eventNodeId = `node-${event.id}-${ts + 3}`;

          const exampleNodes: Node<CanvasNodeData>[] = [
            {
              id: eventNodeId,
              type: 'eventNode',
              position: { x: 300, y: 60 },
              data: { entityId: event.id },
            },
            {
              id: goalNodeId,
              type: 'goalNode',
              position: { x: 300, y: 220 },
              data: { entityId: goal.id },
            },
            {
              id: taskNodeId,
              type: 'taskNode',
              position: { x: 80, y: 390 },
              data: { entityId: task.id },
            },
            {
              id: noteNodeId,
              type: 'noteNode',
              position: { x: 530, y: 390 },
              data: { entityId: note.id },
            },
          ];

          const exampleEdges: Edge[] = [
            {
              id: `ex-e1-${ts}`,
              source: eventNodeId,
              target: goalNodeId,
              type: 'waypoint',
              data: { waypoints: [] },
              style: { stroke: '#52525b' },
            },
            {
              id: `ex-e2-${ts}`,
              source: goalNodeId,
              target: taskNodeId,
              type: 'waypoint',
              data: { waypoints: [] },
              style: { stroke: '#52525b' },
            },
            {
              id: `ex-e3-${ts}`,
              source: goalNodeId,
              target: noteNodeId,
              type: 'waypoint',
              data: { waypoints: [] },
              style: { stroke: '#52525b' },
            },
          ];

          // Remove default placeholder nodes AND any pre-existing canvas nodes for these entities
          // (prevents duplicates on canvas when called more than once)
          const defaultIds = new Set(['node-t1', 'node-n1', 'node-g1']);
          const exampleEntityIds = new Set([goal.id, task.id, note.id, event.id]);
          set((s) => ({
            nodes: [
              ...s.nodes.filter(
                (n) =>
                  !defaultIds.has(n.id) &&
                  !(n.data?.entityId && exampleEntityIds.has(n.data.entityId as string))
              ),
              ...exampleNodes,
            ],
            edges: [
              ...s.edges.filter((e) => !defaultIds.has(e.source) && !defaultIds.has(e.target)),
              ...exampleEdges,
            ],
          }));
        } catch (error) {
          console.error('[VB-020] loadExampleCanvas error:', error);
          throw error;
        }
      },

      onNodesChange: (changes) => {
        set({
          nodes: applyNodeChanges(changes, get().nodes) as Node<CanvasNodeData>[],
        });
      },
      onEdgesChange: (changes) => {
        set({
          edges: applyEdgeChanges(changes, get().edges),
        });
      },
      onConnect: (connection) => {
        if (connection.source === connection.target) return;
        set({
          edges: addEdge(
            {
              ...connection,
              type: 'waypoint',
              data: { waypoints: [] },
              style: { stroke: '#52525b' },
            },
            get().edges
          ),
        });
      },
      addNode: (node) => {
        set({
          nodes: [...get().nodes, node],
        });
      },
      addEdge: (edge) => {
        set((state) => ({
          edges: [...state.edges, edge],
        }));
      },
      updateNodeData: (id, data) => {
        // VB-017 — Auto-push to canvas history for zone rename
        if ('label' in data) {
          const node = get().nodes.find((n) => n.id === id);
          if (node?.type === 'zoneNode') {
            get().pushToCanvasHistory();
          }
        }
        set((state) => ({
          nodes: state.nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...data } } : n)),
        }));
      },
      addToCanvas: (entityId, type, position) =>
        set((state) => {
          const exists = state.nodes.some((n) => n.data.entityId === entityId);
          if (exists) return state;
          const newNode: Node<CanvasNodeData> = {
            id: `node-${entityId}-${Date.now()}`,
            type: `${type}Node`,
            position: position || { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
            data: { entityId },
          };
          return { nodes: [...state.nodes, newNode] };
        }),
      removeFromCanvas: (nodeId) =>
        set((state) => ({
          nodes: state.nodes.filter((n) => n.id !== nodeId),
          edges: state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
        })),
      removeEdge: (edgeId) =>
        set((state) => ({
          edges: state.edges.filter((e) => e.id !== edgeId),
        })),

      // VB-032.5 — Update waypoints for a specific edge
      updateEdgeWaypoints: (edgeId, waypoints) => {
        set((state) => ({
          edges: state.edges.map((e) =>
            e.id === edgeId ? { ...e, data: { ...(e.data || {}), waypoints } } : e
          ),
        }));
      },
      showWaypoints: false,
      toggleShowWaypoints: () => set((s) => ({ showWaypoints: !s.showWaypoints })),

      // Toast notifications
      toasts: [],
      addToast: (message, type) => {
        const id = crypto.randomUUID();
        set((state) => ({ toasts: [...state.toasts, { id, message, type }] }));
        setTimeout(() => get().removeToast(id), 5000);
      },
      removeToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

      // Goal completion modal
      pendingCompletedGoalId: null,
      setPendingCompletedGoalId: (id) => set({ pendingCompletedGoalId: id }),
    }),
    {
      name: 'second-brain-storage',
      partialize: (state) => ({
        tasks: state.tasks,
        notes: state.notes,
        goals: state.goals,
        habits: state.habits,
        events: state.events,
        contacts: state.contacts,
        inboxItems: state.inboxItems,
        // VB-023: nodes and edges intentionally excluded — canvas persisted in Supabase only
      }),
    }
  )
);
